import { useSuspenseQueries, useSuspenseQuery } from '@tanstack/react-query';
import * as React from 'react';

import * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { getIntegrationInstallationKey } from '@gitbook/app-database';
import {
    Alert,
    ButtonSkeleton,
    CardBody,
    CardCheck,
    CardImage,
    CardImageLayout,
    CardRadio,
    CardSkeleton,
    Description,
    Heading,
    IconTile,
    RadioGroup,
    StepControlsSubmitButton,
    toast,
} from '@gitbook/spine-react';
import { ErrorBoundary } from '@gitbook/util-debug';
import { useLoadingState } from '@gitbook/util-react-hooks';
import { assertIsDefined } from '@gitbook/util-ts';

import { apiClient, apiQuery } from '../../../../state';
import { IntegrationIcon, IntegrationStepWrapper } from '../../../Integrations';
import type { SetupFeatureFlagsStepProps } from '../../types';

/**
 * Step to install the feature flags provider integration.
 */
export function InstallIntegrationStep(props: SetupFeatureFlagsStepProps) {
    const headingId = React.useId();
    const [selectedIntegration, setSelectedIntegration] = React.useState<api.Integration | null>(
        null
    );

    return (
        <IntegrationStepWrapper
            title="Select your feature flags provider"
            headingId={headingId}
            description="Choose the provider where you manage your feature flags."
        >
            <ErrorBoundary
                fallback={
                    <Alert
                        kind="danger"
                        title="Error while fetching the feature flags integration"
                        details="Something went wrong while loading the feature flags integrations available. Please try reloading the app."
                    />
                }
            >
                <React.Suspense
                    fallback={
                        <div className="flex flex-col gap-4">
                            <CardSkeleton />
                            <CardSkeleton />
                        </div>
                    }
                >
                    <InstallIntegrationStepContent
                        {...props}
                        headingId={headingId}
                        onSelectedIntegrationChange={setSelectedIntegration}
                    />
                </React.Suspense>
                <div className="mt-auto">
                    <div className="flex w-full justify-end">
                        <React.Suspense fallback={<ButtonSkeleton label="Next step" />}>
                            {selectedIntegration ? (
                                <InstallIntegrationSubmitButton
                                    {...props}
                                    integration={selectedIntegration}
                                />
                            ) : (
                                <StepControlsSubmitButton disabled />
                            )}
                        </React.Suspense>
                    </div>
                </div>
            </ErrorBoundary>
        </IntegrationStepWrapper>
    );
}

function InstallIntegrationStepContent(
    props: SetupFeatureFlagsStepProps & {
        headingId: string;
        onSelectedIntegrationChange: (integration: api.Integration) => void;
    }
) {
    const { data } = useSuspenseQuery(
        apiQuery(
            'listOrganizationIntegrations',
            {
                organizationId: props.organizationId,
                scope: api.IntegrationScope.SiteAdaptiveWrite,
            },
            { nullOnError: true }
        )
    );

    const integrations = data?.items || [];

    return <FeatureFlagsProviderPicker {...props} integrations={integrations} />;
}

/**
 * Display a list of the available feature flags provider to pick from.
 */
function FeatureFlagsProviderPicker(
    props: SetupFeatureFlagsStepProps & {
        headingId: string;
        integrations: api.Integration[];
        onSelectedIntegrationChange: (integration: api.Integration) => void;
    }
) {
    const { integrations, onSelectedIntegrationChange } = props;

    return (
        <RadioGroup
            onChange={(value) => {
                const integration = integrations.find(({ name }) => name === value);
                assertIsDefined(integration, `Picked integration ${value} not found`);
                onSelectedIntegrationChange(integration);
            }}
            aria-labelledby={props.headingId}
        >
            {integrations.map((integration) => (
                <FeatureFlagsProviderCard
                    {...props}
                    key={integration.name}
                    integration={integration}
                />
            ))}
        </RadioGroup>
    );
}

/**
 * Card of a feature flags provider.
 */
function FeatureFlagsProviderCard(
    props: SetupFeatureFlagsStepProps & {
        integration: api.Integration;
    }
) {
    const { integration } = props;

    return (
        <CardRadio value={integration.name} className={'flex items-center gap-4 rounded-md pr-4'}>
            <CardImageLayout>
                <CardImage>
                    <IconTile size={48} className="bg-white">
                        <IntegrationIcon integration={integration} className="size-full" />
                    </IconTile>
                </CardImage>
                <CardBody>
                    <Heading>
                        <div className="flex gap-4">{integration.title}</div>
                    </Heading>
                    <Description>{integration.description}</Description>
                </CardBody>
            </CardImageLayout>
            <CardCheck className="ml-auto" />
        </CardRadio>
    );
}

function InstallIntegrationSubmitButton(
    props: SetupFeatureFlagsStepProps & {
        integration: api.Integration;
    }
) {
    const { organizationId, siteId, integration, onNext } = props;
    const [loadingInstall, wrapLoadingInstall] = useLoadingState();

    const [{ data: orgInstallation }, { data: siteInstallation }] = useSuspenseQueries({
        queries: [
            apiQuery(
                'getIntegrationInstallationById',
                {
                    integrationName: integration.name,
                    installationId: getIntegrationInstallationKey(integration.name, {
                        organization: organizationId,
                    }),
                },
                { nullOnError: true }
            ),
            apiQuery(
                'getIntegrationSiteInstallation',
                {
                    integrationName: integration.name,
                    installationId: getIntegrationInstallationKey(integration.name, {
                        organization: organizationId,
                    }),
                    siteId,
                    extended: true,
                },
                { nullOnError: true }
            ),
        ],
    });

    const isInstalledOnOrg = Boolean(orgInstallation);

    const installIntegrationOnSite = React.useCallback(async () => {
        await toast.catch(async () => {
            await wrapLoadingInstall(async () => {
                let currentInstallationId = isInstalledOnOrg
                    ? getIntegrationInstallationKey(integration.name, {
                          organization: organizationId,
                      })
                    : null;

                if (!currentInstallationId) {
                    const response = await apiClient.integrations.installIntegration(
                        integration.name,
                        {
                            organization: organizationId,
                        }
                    );
                    currentInstallationId = response.data.id;

                    analytics.logEvent({
                        type: 'integration_install',
                        integration: integration.name,
                        organization: organizationId,
                        withSpaceInstallation: false,
                    });
                }

                await apiClient.integrations.installIntegrationOnSite(
                    integration.name,
                    currentInstallationId,
                    { siteId: siteId },
                    { extended: true }
                );

                analytics.logEvent({
                    type: 'integration_site_install',
                    integration: integration.name,
                    organization: organizationId,
                    site: siteId,
                    withAccountInstallation: !isInstalledOnOrg,
                });
            });

            toast.success(`${integration.title} installed`);
        }, 'Error while installing integration');
    }, [
        organizationId,
        siteId,
        integration.name,
        integration.title,
        isInstalledOnOrg,
        wrapLoadingInstall,
    ]);

    return (
        <StepControlsSubmitButton
            loading={loadingInstall}
            onGoNext={async () => {
                if (!siteInstallation) {
                    await installIntegrationOnSite();
                }

                onNext(integration);
            }}
        />
    );
}
